import { useMemo } from "react"

import { useTransportQueryUtils } from "@/transport"

import { PaidOverageReviewPanel } from "../components/PaidOverageReviewPanel"
import type { OverageSettingsPort } from "../hooks/useOverageSettingsEditor"
import type { PaidOverageIdentity } from "./paidOverageAuth"
import { paidOverageReviewAvailability } from "./paidOverageReviewAvailability"
import { createPaidOverageReviewTransport } from "./paidOverageReviewTransport"

interface Props {
  identity: PaidOverageIdentity | null
  surface: "signup" | "quick_enablement"
  settings?: { organizationId: string; port: OverageSettingsPort }
}

function ConnectedReview({
  identity,
  scopeKey,
  surface,
  settings,
}: {
  identity: PaidOverageIdentity
  scopeKey: string
  surface: Props["surface"]
  settings: Props["settings"]
}) {
  const queryUtils = useTransportQueryUtils()
  const {
    clerkOrganizationId,
    organizationSlug,
    actorId,
    expectedOrganizationId,
    getToken,
  } = identity
  const port = useMemo(
    () =>
      createPaidOverageReviewTransport({
        scopeKey,
        identity: {
          clerkOrganizationId,
          organizationSlug,
          actorId,
          expectedOrganizationId,
          getToken,
        },
        invalidate: async () => {
          await Promise.all([
            queryUtils.invalidateResource("billing-overage-settings"),
            queryUtils.invalidateResource("billing-overage-acceptance"),
          ])
        },
      }),
    [
      scopeKey,
      clerkOrganizationId,
      organizationSlug,
      actorId,
      expectedOrganizationId,
      getToken,
      queryUtils,
    ],
  )
  return (
    <PaidOverageReviewPanel
      scopeKey={scopeKey}
      surface={surface}
      port={port}
      settings={settings}
    />
  )
}

export function PaidOverageReviewRoute({ identity, surface, settings }: Props) {
  const scopeKey = JSON.stringify([
    identity?.clerkOrganizationId ?? null,
    identity?.actorId ?? null,
  ])
  if (
    process.env.NEXT_PUBLIC_PAID_OVERAGE_REVIEW_ENABLED !== "true" ||
    identity === null
  ) {
    return (
      <PaidOverageReviewPanel
        scopeKey={scopeKey}
        surface={surface}
        port={paidOverageReviewAvailability}
        canCheckAvailability={false}
      />
    )
  }
  return (
    <ConnectedReview
      identity={identity}
      scopeKey={scopeKey}
      surface={surface}
      settings={settings}
    />
  )
}
